import { motion, useInView, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export const EASE = [0.16, 1, 0.3, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Counter = ({ value, decimals = 0, prefix = "", suffix = "", duration = 1.8, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState((0).toFixed(decimals));

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration,
      ease: EASE,
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return () => controls.stop();
  }, [inView, value, decimals, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{display}{suffix}
    </span>
  );
};

export const SectionHead = ({ index, label, title, sub, dark = false }) => (
  <div className="mb-14 sm:mb-20">
    <Reveal>
      <p className={`overline-label flex items-center gap-3 ${dark ? "text-champagne/70" : ""}`}>
        {index && <span className={dark ? "text-champagne" : "text-moss"}>{index}</span>}
        <span className="inline-block h-px w-10 bg-current opacity-40" aria-hidden="true" />
        {label}
      </p>
    </Reveal>
    <Reveal delay={0.08}>
      <h2 className={`mt-5 font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.04] ${dark ? "text-paper" : "text-ink"}`}>
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.16}>
        <p className={`mt-6 max-w-xl text-base sm:text-lg leading-relaxed ${dark ? "text-paper/60" : "text-smoke"}`}>{sub}</p>
      </Reveal>
    )}
  </div>
);

export const InsightCallout = ({ label = "Analyst takeaway", children, className = "" }) => (
  <Reveal className={`border-l-2 border-champagne pl-6 sm:pl-8 ${className}`}>
    <p className="overline-label text-moss">{label}</p>
    <p className="mt-3 font-serif text-2xl sm:text-3xl font-light leading-snug text-ink">{children}</p>
  </Reveal>
);
